/**
 * FOLLOW THE TURN (v8.324.0) - whether the actor pointer moves when the
 * order does.
 *
 * "Whose turn it is" and "who I am acting as" were two pointers that
 * drifted apart: the tracker advanced, and the GM running ten orks picked
 * the next one by hand. This says when the pointer may follow, and it
 * says so WITHOUT an ownership model (rejected 2026-08-17): a player's
 * pointer is never stolen. It follows when
 *
 *   - I am the GM, for anyone (the GM's cursor rides the order), or
 *   - I was acting as the combatant whose turn just ended, or
 *   - the incoming combatant is in my control network.
 *
 * A viewer preference switches it ON - off by default since v8.331.5,
 * because most people keep to one character, the GM included, and the
 * playtest read a moving pointer as a bug. Pure, zero React dependency.
 */
import type { Combatant } from './roster/types';
import { isControlledBy } from './roster/control';

export interface FollowTurnInput {
  enabled: boolean;
  isGm: boolean;
  /** The combatant whose turn just ended, or null for a custom participant. */
  previousActiveId: string | null;
  /** The combatant whose turn begins, or null for a custom participant. */
  nextActiveId: string | null;
  actorId: string | null;
  roster: readonly Combatant[];
}

/** The combatant to act as now, or null to leave the pointer alone. */
export function followTurnTarget(input: FollowTurnInput): string | null {
  const { enabled, isGm, previousActiveId, nextActiveId, actorId, roster } = input;
  if (!enabled || !nextActiveId || nextActiveId === actorId) return null;
  if (!roster.some((c) => c.id === nextActiveId)) return null;
  if (isGm) return nextActiveId;
  if (actorId && previousActiveId === actorId) return nextActiveId;
  if (actorId && isControlledBy(roster, nextActiveId, actorId)) return nextActiveId;
  return null;
}

/**
 * Did the ORDER ADVANCE - as opposed to merely change?
 *
 * The active combatant also changes when somebody is added to the order,
 * re-sorted by a fresh initiative, or removed; none of those is a turn
 * ending, and following them stole the pointer from a player who had
 * just picked their character (found by the weapon-overrides spec: the
 * GM's pointer jumped to the arrival). A turn has ended when the round
 * moved on, or when the participant that was active is now DONE.
 */
export function turnAdvanced(
  prev: { round: number; activeParticipantId: string | null },
  next: { round: number; participants: readonly { id: string; status: string }[] },
): boolean {
  if (next.round > prev.round) return true;
  if (!prev.activeParticipantId) return false;
  const was = next.participants.find((p) => p.id === prev.activeParticipantId);
  return !!was && was.status === 'done';
}
